import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { ArticleCard } from '@/common/ArticleCard';
import { ArticleListingPage } from '@/common/ArticleListingPage';
import { type ArticleListItem, kindForRole, usePostsFeed } from '@/posts/posts';
import { tutorialsSidebarData } from '@/posts/postsSidebars';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/tutorials/')({
  component: TutorialsScreen,
  validateSearch: (
    search: Record<string, unknown>,
  ): { search_term?: string } => ({
    search_term: search.search_term ? `${search.search_term}` : undefined,
  }),
});

// The v3 posts API has no full-text search, so filter the already-loaded feed
// client-side. Server-side search would need a `search` query param on
// GET /v3/posts.
function filterTutorials(
  items: ArticleListItem[],
  searchTerm?: string,
): ArticleListItem[] {
  if (!searchTerm?.trim()) return items;
  const q = searchTerm.trim().toLowerCase();
  return items.filter(
    (t) =>
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q),
  );
}

export default function TutorialsScreen() {
  const navigate = useNavigate();
  const { search_term } = useSearch({ from: '/_app/tutorials/' });
  const { me, isStudyHelper, isPending } = useCurrentUser();
  // Wait for the role before asking for a kind — a guess fetches the wrong
  // stream and then immediately re-fetches the right one.
  const { items, isLoading, hasMore, loadMore } = usePostsFeed(
    kindForRole(isStudyHelper, 'tutorials'),
    me?.language ?? 'EN',
    { enabled: !isPending },
  );

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const term = new FormData(e.currentTarget).get('search_term') as string;
    navigate({
      to: '/tutorials/',
      search: { search_term: term || undefined },
    });
  };

  const filtered = filterTutorials(items, search_term);

  return (
    <ArticleListingPage
      items={filtered}
      isLoading={isLoading}
      hasMore={hasMore}
      onLoadMore={loadMore}
      sidebarData={tutorialsSidebarData}
      showSearch
      searchTerm={search_term}
      onSearch={handleSearch}
      renderCard={(tutorial) => (
        <ArticleCard
          key={tutorial.slug}
          slug={tutorial.slug}
          title={tutorial.title}
          date={tutorial.date}
          description={tutorial.description}
        />
      )}
    />
  );
}
